// Stande's Terms and Conditions, as published at stande.co/terms.
//
// Kept word for word in step with the marketing site (stande-landing:
// src/app/terms/page.tsx and src/lib/legal.ts). When those change, copy the
// changes here too, so what people agree to when they sign up is what the
// site shows.

export const LEGAL = {
  tradingName: "Stande",
  operatorName: "Stande",
  entityClause: "", // e.g. ", a company registered in England and Wales (company number ...)"
  contactEmail: "admin@stande.co",
  address: "", // registered office / business contact address (leave empty until you have one)
  site: "stande.co",
  app: "app.stande.co",
  updated: "29 July 2026", // Privacy Policy
  termsUpdated: "30 September 2026", // Terms and Conditions (added instructor accounts)
  governingLaw: "England and Wales",
  liabilityCap: "GBP 100",
};

export const TERMS_URL = `https://${LEGAL.site}/terms`;
export const PRIVACY_URL = `https://${LEGAL.site}/privacy`;

const contactAt = LEGAL.address ? ` at ${LEGAL.address}` : "";

export const TERMS_INTRO = [
  `These Terms and Conditions (Terms) govern your access to and use of ${LEGAL.site}, ${LEGAL.app}, and our exam preparation classes, consultations and tools (together, the Services).`,
  `The Services are operated by ${LEGAL.operatorName}${LEGAL.entityClause} (we, us, our). By creating an account, booking a consultation or otherwise using the Services, you agree to these Terms. If you do not agree, please do not use the Services.`,
  `Please also read our Privacy Policy, which explains how we handle your personal data and forms part of these Terms.`,
];

export const TERMS_SECTIONS = [
  {
    id: "about",
    title: "About these terms",
    blocks: [
      {
        p: `These Terms are a legal agreement between you and ${LEGAL.operatorName}${LEGAL.entityClause}. They apply to everyone who uses the Services, including students, the parents or guardians who set up or manage an account, and instructors who use the Services to teach.`,
      },
      {
        p: "We may update these Terms as described in the Changes section below. The version that applies is the one in force when you use the Services.",
      },
    ],
  },
  {
    id: "eligibility",
    title: "Eligibility, age and parental consent",
    blocks: [
      {
        p: "The Services are designed for students preparing for school and college examinations, many of whom are under 18. Access depends on the following conditions.",
      },
      {
        ul: [
          "If you are under 18, you may use the Services only with the consent and supervision of a parent or guardian, who agrees to these Terms on your behalf and is responsible for your use of the Services.",
          "If you are under the minimum age of digital consent in your country, a parent or guardian must set up and manage your account and provide any consent required.",
          "By using the Services, you confirm that you have the legal capacity to agree to these Terms, or that a parent or guardian with that capacity has agreed on your behalf.",
        ],
      },
    ],
  },
  {
    id: "accounts",
    title: "Your account",
    blocks: [
      {
        p: "To use certain features you need an account. You agree to provide accurate information, to keep it up to date, and to keep your login details confidential. You are responsible for activity that takes place under your account.",
      },
      {
        p: "Please tell us promptly if you believe your account has been used without your permission. We may suspend or restrict an account where we reasonably believe it is being misused or these Terms have been breached.",
      },
    ],
  },
  {
    id: "services",
    title: "The services we provide",
    blocks: [
      {
        p: "We provide online exam preparation for United Kingdom based programmes, such as Cambridge and Edexcel IGCSE and A Level. This may include live classes and tuition, exam practice tools with questions and feedback, and consultation bookings.",
      },
      {
        p: "The Services are provided for your personal, non commercial educational use. Instructors may also use them to teach and support their own students, as set out in the Instructor accounts section below. We may add, change, suspend or remove features from time to time.",
      },
    ],
  },
  {
    id: "instructors",
    title: "Instructor accounts",
    blocks: [
      {
        p: "An instructor account lets you create classes, add students, set work and review your students' answers. If you use one, the following also applies to you.",
      },
      {
        ul: [
          "You must be 18 or over.",
          "You may add a student to a class only where you have any authority or consent needed to do so, including from their school, parent or guardian where the student is under 18.",
          "You may use students' names, work and other information you see through the Services only to teach and support those students, and you must not share it outside the Services.",
          "You are responsible for the classes, assignments, comments and other content you create, and for keeping your account secure.",
          "You must not use an instructor account to reach students or information that you are not responsible for.",
        ],
      },
      {
        p: "If you use the Services on behalf of a school or other organisation, you confirm that you are authorised to accept these Terms for it, and these Terms bind that organisation as well as you.",
      },
      {
        p: "We may suspend or remove instructor access where we reasonably believe it is being misused or is putting students at risk.",
      },
    ],
  },
  {
    id: "consultations",
    title: "Consultations and bookings",
    blocks: [
      {
        p: "You can request a consultation through our forms. Submitting a request does not create a binding arrangement until we confirm it. We will use the contact details you provide to respond to your request.",
      },
    ],
  },
  {
    id: "fees",
    title: "Fees, payment and refunds",
    blocks: [
      {
        p: "Some Services are free and others may be paid. Where a Service is paid, the price, billing and cancellation terms will be shown to you before you buy. Payments are handled by third party payment providers.",
      },
      {
        p: "Where you are a consumer, you may have a legal right to cancel certain purchases within a statutory cooling off period, unless the service has been fully performed with your prior agreement. Any such rights, and how to use them, will be explained at the time of purchase.",
      },
      {
        p: "We may change the fees for future purchases. Changes do not affect purchases you have already made.",
      },
    ],
  },
  {
    id: "acceptable-use",
    title: "Acceptable use",
    blocks: [
      { p: "When you use the Services, you agree that you will not:" },
      {
        ul: [
          "use the Services in any way that breaks the law or infringes the rights of others;",
          "share your account or class access with anyone else;",
          "copy, record, resell, publish or redistribute our classes, questions, materials or other content without our permission;",
          "attempt to gain unauthorised access to, disrupt or interfere with the Services or their security;",
          "upload or share content that is unlawful, harmful, misleading or offensive; or",
          "use the Services to cheat, impersonate another person or engage in academic dishonesty.",
        ],
      },
    ],
  },
  {
    id: "your-content",
    title: "Your content",
    blocks: [
      {
        p: "You keep ownership of the work and content you submit through the Services (Your Content). You grant us a worldwide, non exclusive licence to host, store, process and use Your Content in order to provide and improve the Services, including to generate marks and feedback as described in our Privacy Policy.",
      },
      {
        p: "You are responsible for Your Content and confirm that you have the right to submit it and that it does not infringe the rights of others.",
      },
    ],
  },
  {
    id: "intellectual-property",
    title: "Our intellectual property",
    blocks: [
      {
        p: `All classes, recordings, questions, materials, software, text, graphics, logos and the ${LEGAL.tradingName} name and brand are owned by us or our licensors and are protected by intellectual property laws.`,
      },
      {
        p: "We grant you a limited, personal, non transferable and revocable licence to access and use them for your own learning. You may not copy, distribute, publicly share, resell or create derivative works from them without our prior written permission.",
      },
    ],
  },
  {
    id: "no-guarantee",
    title: "Educational disclaimer and no guarantee of results",
    blocks: [
      {
        p: "Our Services are designed to support learning and exam preparation. We do not guarantee any particular grade, examination result, admission or other outcome. Results depend on many factors outside our control, including each student's own effort and circumstances.",
      },
      {
        p: "Any marks or feedback we provide, including automated feedback, are for guidance and study purposes only and are not official examination results.",
      },
    ],
  },
  {
    id: "third-party",
    title: "Third party services",
    blocks: [
      {
        p: "The Services rely on and may link to third party services, such as sign in, hosting, payment and communication tools. We are not responsible for third party services, which are governed by their own terms and policies.",
      },
    ],
  },
  {
    id: "availability",
    title: "Availability and changes to the services",
    blocks: [
      {
        p: "We aim to keep the Services available, but we do not guarantee that they will be uninterrupted, timely, secure or error free. We may modify, suspend or discontinue all or part of the Services at any time.",
      },
    ],
  },
  {
    id: "disclaimers",
    title: "Disclaimers",
    blocks: [
      {
        p: "To the fullest extent permitted by law, the Services are provided on an as is and as available basis, without warranties of any kind, whether express or implied, including any implied warranties of satisfactory quality or fitness for a particular purpose. Nothing in these Terms excludes or limits any rights you have as a consumer that cannot be excluded or limited by law.",
      },
    ],
  },
  {
    id: "liability",
    title: "Limitation of liability",
    blocks: [
      {
        p: "Nothing in these Terms limits or excludes our liability for death or personal injury caused by our negligence, for fraud or fraudulent misrepresentation, or for any other liability that cannot be limited or excluded under applicable law.",
      },
      {
        p: `Subject to the above, we are not liable for any indirect or consequential loss, or for loss of profits, data, goodwill or opportunity. Our total liability arising out of or in connection with the Services is limited to the greater of the amount you paid to us for the Services in the twelve months before the claim, or ${LEGAL.liabilityCap}.`,
      },
      {
        p: "We are not responsible for any failure or delay caused by events beyond our reasonable control.",
      },
    ],
  },
  {
    id: "indemnity",
    title: "Indemnity",
    blocks: [
      {
        p: "To the extent permitted by law, you agree to indemnify us against reasonable claims, losses and costs arising from your misuse of the Services or your breach of these Terms. This does not apply to the extent a loss is caused by us, and nothing in this section affects your rights as a consumer.",
      },
    ],
  },
  {
    id: "termination",
    title: "Suspension and termination",
    blocks: [
      {
        p: "You may stop using the Services and close your account at any time. We may suspend or end your access to the Services if you breach these Terms or misuse the Services. Any provisions that by their nature should continue after termination will continue to apply.",
      },
    ],
  },
  {
    id: "privacy",
    title: "Privacy",
    blocks: [
      {
        p: "Our Privacy Policy explains how we collect, use and protect your personal data. By using the Services you acknowledge that we will handle your data as described there.",
      },
    ],
  },
  {
    id: "changes",
    title: "Changes to these terms",
    blocks: [
      {
        p: "We may update these Terms from time to time. We will post the updated version with a new date, and where changes are significant we will take reasonable steps to notify you. If you continue to use the Services after the changes take effect, you accept the updated Terms.",
      },
    ],
  },
  {
    id: "governing-law",
    title: "Governing law and disputes",
    blocks: [
      {
        p: `These Terms, and any dispute or claim arising out of or in connection with them or the Services, are governed by the laws of ${LEGAL.governingLaw}, and the courts of ${LEGAL.governingLaw} will have jurisdiction.`,
      },
      {
        p: "If you are a consumer resident in another country, you may also benefit from any mandatory protections of the law of your country of residence, and nothing in these Terms affects those rights.",
      },
    ],
  },
  {
    id: "contact",
    title: "Contact us",
    blocks: [
      {
        p: `If you have any questions about these Terms, please contact ${LEGAL.operatorName}${contactAt} by email:`,
      },
      { email: LEGAL.contactEmail },
    ],
  },
];
